/**
 * freezeColumns.ts - Freeze and unfreeze column methods.
 *
 * Freezing only changes `table.frozenColumnCount`, which does not alter cell values, so the change
 * persists with no table op.
 */

import assert from 'assert';

import type {Table} from 'notebook/tables/constants/TableConstants';
import {hasMergeCrossingColumn} from 'notebook/tables/core-ui/FreezeColumnUtils';
import type {TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {persistTable} from 'notebook/tables/store/logic/mutations/mutationParams';
import {saveSelection} from 'notebook/tables/store/logic/mutations/selection';

/**
 * - `ok`: the frozen column count changed.
 * - `noop`: the count already matched, so nothing changed.
 * - `merge-conflict`: a merged cell crosses the new freeze boundary, so nothing changed.
 * - `blocked`: the caller's `canFreeze` check rejected the change, so nothing changed.
 */
export type FreezeColumnsResult = 'ok' | 'noop' | 'merge-conflict' | 'blocked';

export interface FreezeColumnsOptions {
  /**
   * Checked after the count and merge checks pass and before anything is written. Returning
   * `false` aborts with `blocked`.
   */
  canFreeze?: (table: Table, singleInputBoundary: number) => boolean;
  /**
   * Runs after the selection is checkpointed for undo and before the new count is persisted, so
   * the checkpoint captures the selection as it was before this callback.
   */
  beforePersist?: () => void;
}

/**
 * Freeze every column from the single-input boundary through column `j`.
 */
export function freezeThroughColumn(
  params: TableMutationParams,
  j: number,
  options: FreezeColumnsOptions = {}
): FreezeColumnsResult {
  const model = params.getTableModel();
  const {table} = params.getTableProps();
  const siBoundary = model.singleInputs.boundaryIndex ?? 0;
  const newCount = j - siBoundary + 1;
  assert(newCount > 0, `freezeThroughColumn: column ${j} is at or before singleInputBoundary ${siBoundary}`);
  if (newCount === model.affixed.affixedColumnCount) {
    return 'noop';
  }
  if (hasMergeCrossingColumn(table.table, j)) {
    return 'merge-conflict';
  }
  if (options.canFreeze != null && !options.canFreeze(table, siBoundary)) {
    return 'blocked';
  }
  persistFrozenColumnCount(params, table, newCount, options);
  return 'ok';
}

/**
 * Unfreeze column `j` and every frozen column after it.
 */
export function unfreezeFromColumn(
  params: TableMutationParams,
  j: number,
  options: FreezeColumnsOptions = {}
): FreezeColumnsResult {
  const model = params.getTableModel();
  const {table} = params.getTableProps();
  const siBoundary = model.singleInputs.boundaryIndex ?? 0;
  const newCount = j - siBoundary;
  if (newCount >= model.affixed.affixedColumnCount) {
    return 'noop';
  }
  if (newCount > 0 && hasMergeCrossingColumn(table.table, j - 1)) {
    return 'merge-conflict';
  }
  if (options.canFreeze != null && !options.canFreeze(table, siBoundary)) {
    return 'blocked';
  }
  persistFrozenColumnCount(params, table, newCount, options);
  return 'ok';
}

/**
 * `table` is the table read before `beforePersist` runs; it is the one persisted.
 */
function persistFrozenColumnCount(
  params: TableMutationParams,
  table: Table,
  frozenColumnCount: number,
  {beforePersist}: FreezeColumnsOptions
): void {
  saveSelection(params);
  beforePersist?.();
  const {index, updateHandler} = params.getTableProps();
  persistTable(updateHandler, index, table.name, {table: {...table, frozenColumnCount}, tableOp: null});
}
